import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';

/**
 * Access token lives in a module-scoped variable — IN MEMORY ONLY.
 *
 * Spec Part 9: "Never put tokens in localStorage." Anything in localStorage is
 * readable by any XSS on the page. The refresh token is an httpOnly cookie the
 * browser attaches automatically and JS cannot read, so a full reload
 * re-establishes the session via /auth/refresh without ever persisting a token
 * where a script could reach it.
 */
let accessToken: string | null = null;

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '::1']);

function apiOrigin(): string {
  const configured = import.meta.env.VITE_API_ORIGIN as string | undefined;
  if (configured) return configured.replace(/\/$/, '');

  if (typeof window !== 'undefined' && LOCAL_HOSTS.has(window.location.hostname)) {
    return '';
  }

  return 'https://api.maktechacademy.com';
}

function apiUrl(path: string): string {
  return `${apiOrigin()}${path}`;
}

export function setAccessToken(t: string | null): void {
  accessToken = t;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export const api = axios.create({
  baseURL: apiUrl('/api/v1'),
  withCredentials: true, // send the refresh cookie
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
  return config;
});

/**
 * On a 401, try ONE silent refresh and replay the request.
 *
 * The inFlight promise collapses concurrent 401s into a single refresh call —
 * without it, five parallel requests expiring together would fire five
 * rotations, and refresh-token rotation means four of them would present an
 * already-revoked token and trip the server's reuse detection, nuking every
 * session for that user.
 */
let inFlight: Promise<string | null> | null = null;

async function refreshOnce(): Promise<string | null> {
  inFlight ??= (async () => {
    try {
      const r = await axios.post<{ accessToken: string }>(
        apiUrl('/api/v1/auth/refresh'),
        {},
        { withCredentials: true },
      );
      setAccessToken(r.data.accessToken);
      return r.data.accessToken;
    } catch {
      setAccessToken(null);
      return null;
    } finally {
      // Clear on the next tick so callers awaiting this promise all resolve
      // from the same run.
      setTimeout(() => {
        inFlight = null;
      }, 0);
    }
  })();
  return inFlight;
}

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;
    const status = error.response?.status;

    if (status !== 401 || !original || original._retried) throw error;
    // Never try to refresh the refresh call itself.
    if (original.url?.includes('/auth/refresh')) throw error;

    original._retried = true;
    const token = await refreshOnce();
    if (!token) throw error;

    original.headers.Authorization = `Bearer ${token}`;
    return api.request(original);
  },
);

export interface ApiErrorBody {
  error: { code: string; message: string; details?: unknown };
}

/**
 * Pull a USABLE message out of the server's error envelope.
 *
 * Zod failures come back as {code:'VALIDATION_ERROR', message:'Request
 * validation failed', details:[{path,message}]}. The envelope's top-level
 * message is generic boilerplate; the actionable text ("Password must contain
 * a number") is in details. Returning only `message` shows the user something
 * true but useless, so prefer the details when present.
 */
export function errorMessage(e: unknown): string {
  if (e instanceof AxiosError) {
    const body = e.response?.data as ApiErrorBody | undefined;
    const details = body?.error?.details;
    if (Array.isArray(details)) {
      const msgs = details
        .map((d) => (d as { message?: string })?.message)
        .filter((m): m is string => typeof m === 'string' && m.length > 0);
      if (msgs.length) return msgs.join('. ');
    }
    if (body?.error?.message) return body.error.message;
  }
  return e instanceof Error ? e.message : 'Something went wrong';
}
